import type { useTranslations } from 'next-intl';
import type { AeDeliveryWindowState } from './deliveryWindow';
import type { AeEmirate, AeEmirateGroup } from './emirates';

// Narrowed to the 'ring' namespace (not a generic `(key: string) => string`)
// so passing a translator bound to the wrong namespace — e.g. BottomBarAlternate's
// `t` (namespace 'common') instead of its `tRing` — fails typecheck instead of
// silently falling through to a missing-key string at runtime.
type RingTranslator = ReturnType<typeof useTranslations<'ring'>>;

/** Literal keys per branch, not a lookup table — check-i18n-keys only sees
 * quoted first args to t(), same constraint as useSddNddEligibility.ts.
 *
 * No emirate selected and resolver error share one fixed line
 * (defaultSelectEmirates) — the error row is the hard default: on any
 * failure render it, never a positive time-based promise and never a
 * cached state. */
export const aeDeliveryEtaCopy = (
  selectedEmirate: AeEmirate | null,
  group: AeEmirateGroup,
  windowState: AeDeliveryWindowState,
  t: RingTranslator,
): string => {
  if (selectedEmirate === null || windowState === 'error') {
    return t('ring.aeDelivery.defaultSelectEmirates');
  }
  // Emirates without same-day / 4-hour service carry the flat next-day
  // courier promise at every hour — never "not available" copy.
  if (group === 'ineligible' || windowState === 'next_day') {
    return t('ring.aeDelivery.nextDayAvailable');
  }
  if (windowState === 'same_day') {
    return t('ring.aeDelivery.sameDayAvailable');
  }
  // four_hour is reachable for dubai only — abu_dhabi_sharjah's window
  // resolver never produces it (no 4-hour service outside Dubai).
  return t('ring.aeDelivery.fourHourOrderBeforeFivePm');
};

/** Short copy for the bottom bars and order summary — the SDD/NDD bucket
 * only, no "Order now" / cutoff sentence (mirrors India's brief checkout
 * line). same_day and four_hour windows both arrive the same day; the
 * next_day window and ineligible emirates are next-day. Null selection and
 * error share the same fixed default line as the full copy above. */
export const aeDeliveryEtaShortCopy = (
  selectedEmirate: AeEmirate | null,
  group: AeEmirateGroup,
  windowState: AeDeliveryWindowState,
  t: RingTranslator,
): string => {
  if (selectedEmirate === null || windowState === 'error') {
    return t('ring.aeDelivery.defaultSelectEmirates');
  }
  if (group === 'ineligible' || windowState === 'next_day') {
    return t('ring.aeDelivery.nextDayDelivery');
  }
  return t('ring.aeDelivery.sameDayDelivery');
};

/** Gates the BOTTOM BARS' copy of the "Applicable for the sizing kit only"
 * line: it renders there only next to the two positive promise states
 * (4-hour / same-day) after a real selection — never on the default, error,
 * or flat next-day lines, where there is no express promise to qualify. The
 * delivery card's own meta line is permanent and does not use this. */
export const aeDeliveryEtaShowsSizingKitQualifier = (
  selectedEmirate: AeEmirate | null,
  windowState: AeDeliveryWindowState,
): boolean =>
  selectedEmirate !== null &&
  (windowState === 'four_hour' || windowState === 'same_day');

/** Localized display name for a <select> option — same literal-key
 * constraint as above, hence a switch instead of a Record. */
export const aeEmirateLabel = (
  emirate: AeEmirate,
  t: RingTranslator,
): string => {
  switch (emirate) {
    case 'dubai':
      return t('ring.aeDelivery.emirateDubai');
    case 'abu_dhabi':
      return t('ring.aeDelivery.emirateAbuDhabi');
    case 'sharjah':
      return t('ring.aeDelivery.emirateSharjah');
    case 'ajman':
      return t('ring.aeDelivery.emirateAjman');
    case 'ras_al_khaimah':
      return t('ring.aeDelivery.emirateRasAlKhaimah');
    case 'fujairah':
      return t('ring.aeDelivery.emirateFujairah');
    case 'umm_al_quwain':
      return t('ring.aeDelivery.emirateUmmAlQuwain');
  }
};
